import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useStore from "../../lib/store";
import { getBufferRoute } from "../../lib/navigation";

function closeCurrentBuffer(navigate, storeActions) {
  const active = useStore.getState().activeBuffer;
  storeActions.closeBuffer(active);
  navigate(getBufferRoute(useStore.getState().activeBuffer));
  storeActions.closeCommandMode();
}

const COMMANDS = {
  about:      (n) => n("/about"),
  projects:   (n) => n("/projects"),
  resume:     (n) => n("/resume"),
  contact:    (n) => n("/contact"),
  home:       (n) => n("/"),
  dashboard:  (n) => n("/"),
  help:       (n, s) => { s.closeCommandMode(); s.openWhichKey(); },
  keymap:     (_, s) => { s.toggleKeyboardNav(); s.closeCommandMode(); },
  theme:      (_, s, parts) => {
    const sub = parts[1];
    if (sub === "dark" || sub === "light") s.setTheme(sub);
    s.closeCommandMode();
  },
  q:          (n, s) => closeCurrentBuffer(n, s),
  quit:       (n, s) => closeCurrentBuffer(n, s),
};

const CommandMode = () => {
  const navigate = useNavigate();
  const {
    commandModeOpen,
    commandInput,
    commandFeedback,
    setCommandInput,
    setCommandFeedback,
    closeCommandMode,
    openWhichKey,
    toggleKeyboardNav,
    setTheme,
    closeBuffer,
  } = useStore();
  const inputRef = useRef(null);

  useEffect(() => {
    if (commandModeOpen && inputRef.current) inputRef.current.focus();
  }, [commandModeOpen]);

  const handleExecute = (raw) => {
    const trimmed = raw.replace(/^:/, "").trim().toLowerCase();
    const parts = trimmed.split(/\s+/);
    const cmd = parts[0];
    if (!cmd) {
      closeCommandMode();
      return;
    }
    const fn = COMMANDS[cmd];
    if (fn) {
      fn(navigate, { closeCommandMode, openWhichKey, toggleKeyboardNav, setTheme, closeBuffer }, parts);
    } else {
      setCommandFeedback(`E492: Not an editor command: ${cmd}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeCommandMode();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleExecute(commandInput);
    }
  };

  const handleChange = (e) => {
    let v = e.target.value;
    if (!v.startsWith(":")) v = ":" + v;
    setCommandInput(v);
    if (commandFeedback) setCommandFeedback(null);
  };

  return (
    <AnimatePresence>
      {commandModeOpen && (
        <motion.div
          className="commandmode-bar"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <input
            ref={inputRef}
            className="commandmode-input"
            type="text"
            value={commandInput}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            autoComplete="off"
          />
          {commandFeedback && (
            <span className="commandmode-feedback">{commandFeedback}</span>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandMode;